/**
 * Keeps people signed in. Microsoft limits a browser app's refresh token to a day, and phones
 * often block the hidden frame MSAL renews through, so the app also goes back through Microsoft
 * sign-in by itself when it has to. With the Microsoft session still good, that is a quick
 * redirect with no password. It stops when someone signs out here, or when Microsoft won't sign
 * them in: account disabled or deleted, or the password changed.
 */

const rememberKey = "expenses.signed-in-as";
const attemptKey = "expenses.sign-in-attempted-at";

/** Long enough to catch a redirect that comes straight back failing, short enough to retry later. */
const RetryAfterMs = 2 * 60 * 1000;

export function rememberSignIn(userName: string): void {
  try {
    window.localStorage.setItem(rememberKey, userName);
  } catch {
    // Storage blocked: the person signs in by hand next time.
  }
}

export function forgetSignIn(): void {
  try {
    window.localStorage.removeItem(rememberKey);
    window.sessionStorage.removeItem(attemptKey);
  } catch {
    // ignore
  }
}

/** Who was signed in on this device and hasn't signed out. */
export function rememberedUser(): string | null {
  try {
    return window.localStorage.getItem(rememberKey);
  } catch {
    return null;
  }
}

/**
 * Whether the app may send the person through Microsoft sign-in on its own now. Records the
 * attempt, so a sign-in that keeps failing can't bounce back and forth.
 */
export function claimSignInAttempt(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(attemptKey) ?? "0");
    if (Date.now() - last < RetryAfterMs) return false;
    window.sessionStorage.setItem(attemptKey, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

/** A token came back fine, so the next failure may sign in again straight away. */
export function clearSignInAttempt(): void {
  try {
    window.sessionStorage.removeItem(attemptKey);
  } catch {
    // ignore
  }
}
